import { useEffect, useRef, useState, type ChangeEvent } from "react";
import {
  Check,
  Copy,
  FileCode2,
  Palette,
  RefreshCw,
  Send,
  ShieldAlert,
  Trash2,
  Upload,
} from "lucide-react";
import { SHOWCASE_DESIGN_CONTRACT_VERSION } from "../../../lib/showcaseDesignContract";
import { driverApi, type FlowDesign, type FlowDesignUpdate } from "../lib/api";
import {
  flowDesignErrorMessage,
  flowDesignValidationDiagnostic,
  type FlowDesignValidationDiagnostic,
} from "../lib/flowDesignErrors";
import {
  FLOW_DESIGN_CSS_POLICY_VERSION,
  FLOW_DESIGN_PROMPT,
} from "../lib/flowDesignPrompt";

export const FLOW_DESIGN_FALLBACK_MAX_SIZE_BYTES = 65_536;
const FLOW_DESIGN_RAW_TRANSPORT_MAX_SIZE_BYTES = 512 * 1024;

type Mutation = "upload" | "delete" | "send";
type CopyState = "copied" | "manual" | null;
type ActiveMutation = {
  id: number;
  flowId: number;
  kind: Mutation;
};
type MetadataRequest = {
  id: number;
  flowId: number;
};

export function FlowDesignSection({
  flowId,
  flowName,
}: {
  flowId: number;
  flowName?: string;
}) {
  const [designSnapshot, setDesignSnapshot] = useState<{
    flowId: number;
    design: FlowDesign;
  } | null>(null);
  const [loadFlowId, setLoadFlowId] = useState(flowId);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [mutation, setMutation] = useState<Mutation | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [validationDiagnostic, setValidationDiagnostic] =
    useState<FlowDesignValidationDiagnostic | null>(null);
  const [repairManualPrompt, setRepairManualPrompt] = useState<string | null>(
    null,
  );
  const [contractMismatchLatched, setContractMismatchLatched] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState(false);
  const [promptExpanded, setPromptExpanded] = useState(false);
  const [copyState, setCopyState] = useState<CopyState>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const promptRef = useRef<HTMLTextAreaElement>(null);
  const repairPromptRef = useRef<HTMLTextAreaElement>(null);
  const flowIdRef = useRef(flowId);
  const viewGenerationRef = useRef(0);
  const metadataRequestRef = useRef<MetadataRequest | null>(null);
  const metadataRequestSequenceRef = useRef(0);
  const mutationRef = useRef<ActiveMutation | null>(null);
  const mutationSequenceRef = useRef(0);
  flowIdRef.current = flowId;

  useEffect(() => {
    const requestFlowId = flowId;
    viewGenerationRef.current += 1;
    const request = beginMetadataRequest(requestFlowId);
    let cancelled = false;
    setDesignSnapshot(null);
    setLoadFlowId(requestFlowId);
    setLoading(true);
    setLoadFailed(false);
    setError(null);
    setValidationDiagnostic(null);
    setRepairManualPrompt(null);
    setContractMismatchLatched(false);
    setNotice(null);
    setDeleteConfirmation(false);
    setPromptExpanded(false);
    setCopyState(null);
    mutationRef.current = null;
    setMutation(null);
    if (inputRef.current) inputRef.current.value = "";
    void driverApi
      .getFlowDesign(requestFlowId)
      .then((response) => {
        if (cancelled || !metadataRequestIsCurrent(request)) return;
        setLoading(false);
        if (response.error || !response.data) {
          setLoadFailed(true);
          setError(
            flowDesignErrorMessage(
              response.error,
              "не удалось загрузить данные дизайна",
            ),
          );
          return;
        }
        setDesignSnapshot({ flowId: requestFlowId, design: response.data });
      })
      .catch((caught: unknown) => {
        if (cancelled || !metadataRequestIsCurrent(request)) return;
        setLoading(false);
        setLoadFailed(true);
        setError(
          flowDesignErrorMessage(caught, "не удалось загрузить данные дизайна"),
        );
      });
    return () => {
      cancelled = true;
    };
  }, [flowId]);

  useEffect(() => {
    if (copyState !== "manual" || !promptExpanded) return;
    promptRef.current?.focus();
    promptRef.current?.select();
  }, [copyState, promptExpanded]);

  useEffect(() => {
    if (!repairManualPrompt) return;
    repairPromptRef.current?.focus();
    repairPromptRef.current?.select();
  }, [repairManualPrompt]);

  const design =
    designSnapshot?.flowId === flowId ? designSnapshot.design : null;
  const metadataLoading = loading || loadFlowId !== flowId;
  const locked = mutation !== null;
  const hasDesign = Boolean(design?.has_custom_css);
  const designBlocked = Boolean(design?.custom_css_blocked);
  const storedDesignCompatible = Boolean(
    !hasDesign || design?.custom_css_compatible,
  );
  const contractMismatch = Boolean(
    contractMismatchLatched ||
    (design &&
      (design.contract_version !== Number(SHOWCASE_DESIGN_CONTRACT_VERSION) ||
        design.css_policy_version !== FLOW_DESIGN_CSS_POLICY_VERSION)),
  );
  const metadataCompatible = Boolean(design && !contractMismatch);
  const maxSizeBytes =
    design && design.max_size_bytes > 0
      ? design.max_size_bytes
      : FLOW_DESIGN_FALLBACK_MAX_SIZE_BYTES;
  const displayFlowName = flowName?.trim() || `поток #${flowId}`;

  useEffect(() => {
    if (metadataCompatible) return;
    setPromptExpanded(false);
    setCopyState(null);
  }, [metadataCompatible]);

  function beginMutation(next: Mutation): ActiveMutation | null {
    if (
      mutationRef.current !== null ||
      !design ||
      (next === "upload" && !metadataCompatible)
    )
      return null;
    const operation = {
      id: mutationSequenceRef.current + 1,
      flowId,
      kind: next,
    };
    mutationSequenceRef.current = operation.id;
    mutationRef.current = operation;
    setMutation(next);
    setError(null);
    setValidationDiagnostic(null);
    setRepairManualPrompt(null);
    setNotice(null);
    return operation;
  }

  function finishMutation(operation: ActiveMutation) {
    if (mutationRef.current?.id !== operation.id) return;
    mutationRef.current = null;
    setMutation(null);
  }

  function operationIsCurrent(operation: ActiveMutation): boolean {
    return (
      flowIdRef.current === operation.flowId &&
      mutationRef.current?.id === operation.id
    );
  }

  function beginMetadataRequest(targetFlowId: number): MetadataRequest {
    const request = {
      id: metadataRequestSequenceRef.current + 1,
      flowId: targetFlowId,
    };
    metadataRequestSequenceRef.current = request.id;
    metadataRequestRef.current = request;
    return request;
  }

  function metadataRequestIsCurrent(request: MetadataRequest): boolean {
    return (
      flowIdRef.current === request.flowId &&
      metadataRequestRef.current?.id === request.id
    );
  }

  async function refreshDesignMetadata(targetFlowId: number): Promise<boolean> {
    const request = beginMetadataRequest(targetFlowId);
    try {
      const response = await driverApi.getFlowDesign(targetFlowId);
      if (!metadataRequestIsCurrent(request)) return false;
      if (response.error || !response.data) return false;
      setDesignSnapshot({ flowId: targetFlowId, design: response.data });
      return true;
    } catch {
      return false;
    }
  }

  async function reload() {
    if (metadataLoading) return;
    const requestFlowId = flowId;
    const request = beginMetadataRequest(requestFlowId);
    setLoadFlowId(requestFlowId);
    setLoading(true);
    setLoadFailed(false);
    setError(null);
    try {
      const response = await driverApi.getFlowDesign(requestFlowId);
      if (!metadataRequestIsCurrent(request)) return;
      if (response.error || !response.data) {
        setLoadFailed(true);
        setError(
          flowDesignErrorMessage(
            response.error,
            "не удалось загрузить данные дизайна",
          ),
        );
        return;
      }
      setDesignSnapshot({ flowId: requestFlowId, design: response.data });
    } catch (caught) {
      if (!metadataRequestIsCurrent(request)) return;
      setLoadFailed(true);
      setError(
        flowDesignErrorMessage(caught, "не удалось загрузить данные дизайна"),
      );
    } finally {
      if (metadataRequestIsCurrent(request)) setLoading(false);
    }
  }

  function chooseFile() {
    if (mutationRef.current !== null || designBlocked || !metadataCompatible)
      return;
    setDeleteConfirmation(false);
    inputRef.current?.click();
  }

  async function onFileChange(event: ChangeEvent<HTMLInputElement>) {
    const input = event.currentTarget;
    const file = input.files?.[0];
    input.value = "";
    if (!file) return;
    const operation = beginMutation("upload");
    if (!operation) return;
    const replacedExistingDesign = hasDesign;
    const uploadMaxSizeBytes = maxSizeBytes;
    const uploadRevision = design!.revision;

    try {
      const payload = await readFlowDesignFile(file, uploadMaxSizeBytes);
      if (!operationIsCurrent(operation)) return;
      const response = await driverApi.updateFlowDesign(operation.flowId, {
        ...payload,
        revision: uploadRevision,
      });
      if (!operationIsCurrent(operation)) return;
      if (response.error || !response.data) {
        const responseCode = errorCode(response.error);
        if (responseCode === "flow_design_contract_mismatch") {
          setContractMismatchLatched(true);
        }
        if (
          responseCode === "flow_design_blocked" ||
          responseCode === "flow_design_contract_mismatch" ||
          responseCode === "flow_design_revision_mismatch"
        ) {
          await refreshDesignMetadata(operation.flowId);
        }
        if (!operationIsCurrent(operation)) return;
        const diagnostic = flowDesignValidationDiagnostic(response.error);
        setValidationDiagnostic(diagnostic);
        setError(
          flowDesignErrorMessage(
            response.error,
            "не удалось сохранить style.css",
          ),
        );
        return;
      }
      setDesignSnapshot({
        flowId: operation.flowId,
        design: response.data,
      });
      setDeleteConfirmation(false);
      setNotice(
        replacedExistingDesign
          ? "style.css заменён и уже применяется на витрине"
          : "style.css загружен и уже применяется на витрине",
      );
    } catch (caught) {
      if (operationIsCurrent(operation)) {
        setError(
          flowDesignErrorMessage(caught, "не удалось прочитать CSS-файл"),
        );
      }
    } finally {
      finishMutation(operation);
    }
  }

  async function removeDesign() {
    const operation = beginMutation("delete");
    if (!operation) return;
    const previousDesign = design;
    const previousMaxSizeBytes = maxSizeBytes;
    const deleteRevision = design!.revision;
    try {
      const response = await driverApi.deleteFlowDesign(
        operation.flowId,
        deleteRevision,
      );
      if (!operationIsCurrent(operation)) return;
      if (response.error) {
        if (errorCode(response.error) === "flow_design_revision_mismatch") {
          await refreshDesignMetadata(operation.flowId);
        }
        if (!operationIsCurrent(operation)) return;
        setError(
          flowDesignErrorMessage(
            response.error,
            "не удалось удалить style.css",
          ),
        );
        return;
      }
      const refreshed = await refreshDesignMetadata(operation.flowId);
      if (!operationIsCurrent(operation)) return;
      if (!refreshed) {
        setDesignSnapshot({
          flowId: operation.flowId,
          design: {
            has_custom_css: false,
            filename: null,
            size_bytes: 0,
            updated_at: null,
            max_size_bytes: previousMaxSizeBytes,
            contract_version:
              previousDesign?.contract_version ??
              Number(SHOWCASE_DESIGN_CONTRACT_VERSION),
            css_policy_version:
              previousDesign?.css_policy_version ??
              FLOW_DESIGN_CSS_POLICY_VERSION,
            custom_css_blocked: previousDesign?.custom_css_blocked ?? false,
            custom_css_blocked_at:
              previousDesign?.custom_css_blocked_at ?? null,
            stored_css_policy_version: null,
            custom_css_compatible: true,
            revision: deleteRevision + 1,
          },
        });
      }
      setDeleteConfirmation(false);
      setNotice("дизайн удалён, витрина использует стандартное оформление");
    } catch (caught) {
      if (operationIsCurrent(operation)) {
        setError(
          flowDesignErrorMessage(caught, "не удалось удалить style.css"),
        );
      }
    } finally {
      finishMutation(operation);
    }
  }

  async function sendToBot() {
    const operation = beginMutation("send");
    if (!operation) return;
    setDeleteConfirmation(false);
    try {
      const response = await driverApi.sendFlowDesign(operation.flowId);
      if (!operationIsCurrent(operation)) return;
      if (response.error || !response.data?.ok) {
        if (errorCode(response.error) === "flow_design_blocked") {
          await refreshDesignMetadata(operation.flowId);
        }
        if (!operationIsCurrent(operation)) return;
        setError(
          flowDesignErrorMessage(
            response.error,
            "не удалось отправить style.css в бота",
          ),
        );
        return;
      }
      setNotice("style.css отправлен в чат с ботом заливщика");
    } catch (caught) {
      if (operationIsCurrent(operation)) {
        setError(
          flowDesignErrorMessage(
            caught,
            "не удалось отправить style.css в бота",
          ),
        );
      }
    } finally {
      finishMutation(operation);
    }
  }

  async function copyPrompt() {
    if (!metadataCompatible) return;
    const promptFlowId = flowId;
    const promptGeneration = viewGenerationRef.current;
    setCopyState(null);
    setNotice(null);
    try {
      if (!navigator.clipboard?.writeText)
        throw new Error("clipboard unavailable");
      await navigator.clipboard.writeText(FLOW_DESIGN_PROMPT);
      if (
        flowIdRef.current !== promptFlowId ||
        viewGenerationRef.current !== promptGeneration
      )
        return;
      setCopyState("copied");
    } catch {
      if (
        flowIdRef.current !== promptFlowId ||
        viewGenerationRef.current !== promptGeneration
      )
        return;
      setPromptExpanded(true);
      setCopyState("manual");
    }
  }

  async function copyRepairPrompt() {
    if (!validationDiagnostic) return;
    const promptFlowId = flowId;
    const promptGeneration = viewGenerationRef.current;
    const diagnostic = validationDiagnostic;
    try {
      if (!navigator.clipboard?.writeText)
        throw new Error("clipboard unavailable");
      await navigator.clipboard.writeText(diagnostic.repairPrompt);
      if (
        flowIdRef.current !== promptFlowId ||
        viewGenerationRef.current !== promptGeneration
      )
        return;
      setRepairManualPrompt(null);
      setNotice(
        "запрос скопирован — вставьте его в тот же чат с ИИ и снова приложите отклонённый style.css",
      );
    } catch {
      if (
        flowIdRef.current !== promptFlowId ||
        viewGenerationRef.current !== promptGeneration
      )
        return;
      setRepairManualPrompt(diagnostic.repairPrompt);
      setNotice("автоматическое копирование недоступно — запрос выделен ниже");
    }
  }

  return (
    <section
      className="tp-flow-design-section"
      aria-labelledby="flow-design-title"
    >
      <input
        ref={inputRef}
        data-testid="flow-design-file-input"
        type="file"
        accept=".css,text/css"
        hidden
        disabled={designBlocked || !metadataCompatible}
        onChange={onFileChange}
      />

      <div className="tp-flow-design-main-card">
        <div className="tp-flow-design-heading">
          <span className="tp-flow-design-heading-icon">
            <Palette aria-hidden="true" />
          </span>
          <h2 id="flow-design-title">дизайн витрины</h2>
          <span className="tp-flow-design-flow-name" title={displayFlowName}>
            {displayFlowName}
          </span>
        </div>
        <p className="tp-flow-design-subtitle">
          сделайте витрину похожей на ваш mini app
        </p>

        <div className="tp-flow-design-steps">
          <div className="tp-flow-design-step">
            <span className="tp-flow-design-step-number" aria-hidden="true">
              1
            </span>
            <div className="tp-flow-design-step-copy">
              <h3>создать дизайн через ai</h3>
              <p>
                скопируйте промпт и отправьте его вместе с html своего mini app
                в chatgpt, claude или gemini
              </p>
            </div>
            <button
              type="button"
              className="tp-flow-design-icon-button"
              disabled={!metadataCompatible}
              aria-label={
                copyState === "copied"
                  ? "промпт скопирован"
                  : "скопировать промпт"
              }
              title={
                copyState === "copied"
                  ? "промпт скопирован"
                  : "скопировать промпт"
              }
              onClick={() => void copyPrompt()}
            >
              {copyState === "copied" ? (
                <Check aria-hidden="true" />
              ) : (
                <Copy aria-hidden="true" />
              )}
            </button>
          </div>

          <div className="tp-flow-design-divider" aria-hidden="true" />

          <div className="tp-flow-design-step">
            <span className="tp-flow-design-step-number" aria-hidden="true">
              2
            </span>
            <div className="tp-flow-design-step-copy">
              <h3>загрузите style.css</h3>
              <p>
                загрузите готовый style.css, чтобы витрина выглядела как ваш
                mini app
              </p>
            </div>
            <button
              type="button"
              className="tp-flow-design-icon-button"
              disabled={locked || designBlocked || !metadataCompatible}
              aria-label={
                mutation === "upload"
                  ? hasDesign
                    ? "заменяем..."
                    : "загружаем..."
                  : hasDesign
                    ? "заменить"
                    : "загрузить css"
              }
              title={hasDesign ? "заменить style.css" : "загрузить style.css"}
              aria-busy={mutation === "upload" || undefined}
              onClick={chooseFile}
            >
              <Upload aria-hidden="true" />
            </button>
          </div>
        </div>

        {copyState === "manual" && metadataCompatible ? (
          <p className="tp-flow-design-copy-fallback" role="status">
            автоматическое копирование недоступно. текст выделен — скопируйте
            его вручную
          </p>
        ) : null}
        {promptExpanded && metadataCompatible ? (
          <textarea
            ref={promptRef}
            id="flow-design-prompt-text"
            className="tp-flow-design-prompt-text"
            aria-label="готовый промпт для создания style.css"
            readOnly
            spellCheck={false}
            value={FLOW_DESIGN_PROMPT}
          />
        ) : null}
      </div>

      {metadataLoading ? (
        <div className="tp-flow-design-loading" role="status">
          загружаем данные дизайна...
        </div>
      ) : loadFailed && !design ? (
        <button
          type="button"
          className="tp-button-secondary w-full gap-2"
          disabled={metadataLoading}
          onClick={() => void reload()}
        >
          <RefreshCw aria-hidden="true" />
          <span>повторить загрузку</span>
        </button>
      ) : designBlocked && design ? (
        <div className="tp-flow-design-blocked-card" role="status">
          <ShieldAlert aria-hidden="true" />
          <div>
            <strong>пользовательский CSS отключён администратором</strong>
            <p>
              витрина использует стандартное оформление. Чтобы снова загружать
              дизайн, обратитесь в поддержку trafsy
            </p>
          </div>
        </div>
      ) : hasDesign && design ? (
        <div className="tp-flow-design-file-card">
          <div className="tp-flow-design-file-body">
            <div className="tp-flow-design-file-heading">
              <FileCode2 aria-hidden="true" />
              <h3>загружен файл</h3>
            </div>
            <dl className="tp-flow-design-file-meta">
              <div>
                <dt>файл:</dt>
                <dd>style.css</dd>
              </div>
              <div>
                <dt>размер:</dt>
                <dd>{formatBytes(design.size_bytes)}</dd>
              </div>
            </dl>
          </div>

          <div className="tp-flow-design-actions">
            <button
              type="button"
              className="tp-flow-design-icon-button"
              disabled={locked}
              aria-label={
                mutation === "send" ? "отправляем..." : "отправить в бота"
              }
              title="отправить в бота"
              aria-busy={mutation === "send" || undefined}
              onClick={() => void sendToBot()}
            >
              <Send aria-hidden="true" />
            </button>
            <button
              type="button"
              className="tp-flow-design-icon-button tp-flow-design-icon-button-danger"
              disabled={locked}
              aria-label="удалить"
              title="удалить"
              aria-busy={mutation === "delete" || undefined}
              onClick={() => setDeleteConfirmation(true)}
            >
              <Trash2 aria-hidden="true" />
            </button>
          </div>

          {!storedDesignCompatible ? (
            <div className="tp-alert" role="status">
              Этот style.css создан по предыдущей версии правил и временно не
              применяется на витрине. Замените файл новым; сохранённый файл
              по-прежнему можно отправить себе в бота или удалить.
            </div>
          ) : null}

          {deleteConfirmation ? (
            <div
              className="tp-flow-design-delete-confirmation"
              role="group"
              aria-label="подтверждение удаления"
            >
              <p>удалить style.css и вернуть стандартный дизайн витрины?</p>
              <div>
                <button
                  type="button"
                  className="tp-button-secondary"
                  disabled={locked}
                  onClick={() => setDeleteConfirmation(false)}
                >
                  отмена
                </button>
                <button
                  type="button"
                  className="tp-button-danger"
                  disabled={locked}
                  onClick={() => void removeDesign()}
                >
                  {mutation === "delete"
                    ? "удаляем..."
                    : "подтвердить удаление"}
                </button>
              </div>
            </div>
          ) : null}
        </div>
      ) : (
        <p className="tp-flow-design-standard-note">
          <span aria-hidden="true">*</span>
          <span>сейчас используется стандартный дизайн trafsy</span>
        </p>
      )}

      {error ? (
        <div className="tp-alert" role="alert">
          <p>{error}</p>
          {validationDiagnostic ? (
            <>
              {hasDesign ? (
                <p>
                  {storedDesignCompatible
                    ? "предыдущий style.css сохранён и продолжает работать"
                    : "предыдущий style.css сохранён, но не применяется из-за старой версии правил"}
                </p>
              ) : null}
              <button
                type="button"
                className="tp-button-secondary tp-flow-design-repair-copy"
                onClick={() => void copyRepairPrompt()}
              >
                <Copy aria-hidden="true" />
                <span>скопировать запрос на исправление</span>
              </button>
              {repairManualPrompt ? (
                <textarea
                  ref={repairPromptRef}
                  className="tp-flow-design-prompt-text"
                  aria-label="запрос на исправление style.css"
                  readOnly
                  spellCheck={false}
                  value={repairManualPrompt}
                />
              ) : null}
            </>
          ) : null}
        </div>
      ) : null}
      {contractMismatch ? (
        <div className="tp-alert" role="alert">
          версия правил дизайна обновилась. Закройте и заново откройте Mini App,
          прежде чем копировать промпт или загружать новый CSS
        </div>
      ) : null}
      {notice ? (
        <div className="tp-flow-design-notice" role="status">
          <Check aria-hidden="true" />
          <span>{notice}</span>
        </div>
      ) : null}
    </section>
  );
}

export async function readFlowDesignFile(
  file: File,
  maxSizeBytes: number,
): Promise<FlowDesignUpdate> {
  if (!file.name.toLowerCase().endsWith(".css")) {
    throw new Error("выберите файл с расширением .css");
  }

  // Reject an accidentally huge file before allocating it in Telegram WebView.
  // The authoritative limit below is checked after BOM/fence normalization.
  const rawTransportLimit = Math.max(
    FLOW_DESIGN_RAW_TRANSPORT_MAX_SIZE_BYTES,
    maxSizeBytes * 2,
  );
  if (file.size > rawTransportLimit) {
    throw new Error(
      `CSS-файл слишком большой: после очистки он должен быть не больше ${formatBytes(maxSizeBytes)}`,
    );
  }

  const bytes = await readFileBytes(file);
  let css: string;
  try {
    css = new TextDecoder("utf-8", { fatal: true }).decode(bytes);
  } catch {
    throw new Error("CSS-файл должен быть сохранён в кодировке UTF-8");
  }
  if (css.startsWith("\ufeff")) css = css.slice(1);
  const fencedCss = css.match(
    /^[ \t\r\n]*```css[ \t]*(?:\r\n|\n|\r)([\s\S]*?)(?:\r\n|\n|\r)```[ \t\r\n]*$/i,
  );
  if (fencedCss) {
    css = fencedCss[1] ?? "";
    if (css.startsWith("\ufeff")) css = css.slice(1);
  }
  if (!css.trim()) {
    throw new Error("CSS-файл не должен быть пустым");
  }
  if (new TextEncoder().encode(css).byteLength > maxSizeBytes) {
    throw new Error(
      `CSS-файл должен быть не больше ${formatBytes(maxSizeBytes)}`,
    );
  }
  return {
    filename: file.name,
    css,
    contract_version: Number(SHOWCASE_DESIGN_CONTRACT_VERSION),
    css_policy_version: FLOW_DESIGN_CSS_POLICY_VERSION,
  };
}

function readFileBytes(file: File): Promise<ArrayBuffer> {
  if (typeof file.arrayBuffer === "function") return file.arrayBuffer();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () =>
      reject(reader.error ?? new Error("file read failed"));
    reader.onload = () => {
      if (reader.result instanceof ArrayBuffer) {
        resolve(reader.result);
      } else {
        reject(new Error("file read failed"));
      }
    };
    reader.readAsArrayBuffer(file);
  });
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} Б`;
  const value = bytes / 1024;
  const rounded = value < 10 ? value.toFixed(1) : value.toFixed(0);
  return `${rounded.replace(".", ",")} КБ`;
}

function errorCode(error: unknown): string | undefined {
  if (!error || typeof error !== "object") return undefined;
  const payload = error as {
    error?: { code?: string };
    detail?: { code?: string };
  };
  return payload.error?.code ?? payload.detail?.code;
}
